import { NavigationItem } from "@/navigation/types";
import hubConfig from "@/navigation/hubNavigation";
import adminConfig from "@/navigation/adminNavigation";
import labsConfig from "@/navigation/labsNavigation";
import stochasticConfig from "@/navigation/stochasticNavigation";
import eventRegistrationConfig from "@/navigation/eventRegistrationNavigation";
import dataConnectorConfig from "@/navigation/dataConnectorNavigation";
import callIntelligenceConfig from "@/navigation/callIntelligenceNavigation";
import partnerNetworkConfig from "@/navigation/partnerNetworkNavigation";
import communityConfig from "@/navigation/communityNavigation";
import learningCenterConfig from "@/navigation/learningCenterNavigation";
import productStorefrontConfig from "@/navigation/productStorefrontNavigation";
import transcriptConfig from "@/navigation/transcriptNavigation";
import salesConfig from "@/navigation/salesNavigation";
import {
  HomeIcon,
  Squares2X2Icon,
  Cog6ToothIcon,
  UsersIcon,
  AcademicCapIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  ChatBubbleLeftRightIcon,
  ShoppingCartIcon,
  ServerStackIcon,
  LightBulbIcon,
} from "@heroicons/react/24/outline";
import { GraduationCap } from "lucide-react";

export function getUnifiedNavigation(): NavigationItem[] {
  return [
    {
      name: "Hub",
      internalName: "hub",
      href: "#",
      icon: HomeIcon,
      current: false,
      children: hubConfig.navigation,
    },
    {
      name: "Lead Generation",
      internalName: "stochastic",
      href: "#",
      icon: Squares2X2Icon,
      current: false,
      children: stochasticConfig.navigation,
    },
    {
      name: "Call Intelligence",
      internalName: "call-intelligence",
      permissions: [],
      href: "/calls",
      icon: PhoneIcon,
      current: false,
      roles: [
        "ROLE_SUPER_ADMIN",
        "ROLE_SALES_MANAGER",
        "ROLE_SALES_REP",
        "ROLE_ADMIN",
        "ROLE_OPERATIONS",
        "ROLE_SUPPORT",
      ],
      children: callIntelligenceConfig.navigation,
    },
    {
      name: "Sales",
      internalName: "sales",
      permissions: [],
      href: "/sales/pipeline",
      icon: CurrencyDollarIcon,
      current: false,
      roles: ["ROLE_SUPER_ADMIN", "ROLE_SALES_MANAGER", "ROLE_SALES_REP"],
      children: salesConfig.navigation,
    },
    {
      name: "Events",
      internalName: "events",
      permissions: [],
      href: "/event-registration/events",
      icon: CalendarDaysIcon,
      current: false,
      roles: ["ROLE_SUPER_ADMIN"],
      children: eventRegistrationConfig.navigation[0]?.children || [],
    },
    {
      name: "Product Store",
      internalName: "product-storefront",
      permissions: [],
      href: "/product-storefront/products",
      icon: ShoppingCartIcon,
      current: false,
      roles: ["ROLE_SUPER_ADMIN"],
      children: productStorefrontConfig.navigation[0]?.children || [],
    },
    {
      name: "Learning Center",
      internalName: "learning-center",
      permissions: [],
      href: "/hub/learning-center/directory",
      icon: GraduationCap,
      current: false,
      children: learningCenterConfig.navigation,
    },
    {
      name: "Labs",
      internalName: "labs",
      permissions: [],
      href: "/games/door-to-done",
      icon: LightBulbIcon,
      current: false,
      children: labsConfig.navigation,
    },
    {
      name: "Partner Network",
      internalName: "partner_network",
      permissions: [],
      href: "#",
      icon: BuildingOfficeIcon,
      current: false,
      children: partnerNetworkConfig.navigation,
    },
    {
      name: "Community",
      internalName: "community",
      permissions: [],
      href: "/hub/community",
      icon: ChatBubbleLeftRightIcon,
      current: false,
      children: communityConfig.navigation,
    },
    {
      name: "Transcription",
      internalName: "transcripts",
      permissions: [],
      href: "#",
      icon: Squares2X2Icon,
      current: false,
      roles: ["ROLE_SUPER_ADMIN"],
      children: transcriptConfig.navigation,
      isCertainPathOnly: true,
    },
    {
      name: "Internal Admin",
      internalName: "admin",
      href: "#",
      icon: Cog6ToothIcon,
      current: false,
      roles: ["ROLE_SUPER_ADMIN", "ROLE_MARKETING"],
      children: adminConfig.navigation,
      isCertainPathOnly: true,
    },
    // Legacy modules - being replaced by Call Intelligence
    // {
    //   name: "Sales Coaching",
    //   internalName: "sales-coaching",
    //   permissions: [],
    //   href: "#",
    //   icon: ChartBarIcon,
    //   current: false,
    //   roles: ["ROLE_SUPER_ADMIN", "ROLE_SALES_MANAGER", "ROLE_SALES_REP"],
    //   children: salesCoachingConfig.navigation,
    //   isCertainPathOnly: true,
    // },
    // {
    //   name: "Transcription",
    //   internalName: "transcripts",
    //   permissions: [],
    //   href: "#",
    //   icon: Squares2X2Icon,
    //   current: false,
    //   roles: ["ROLE_SUPER_ADMIN"],
    //   children: transcriptConfig.navigation,
    // },
    {
      name: "Event Registration",
      internalName: "event-registration",
      href: "#",
      icon: CalendarDaysIcon,
      current: false,
      roles: ["ROLE_SUPER_ADMIN"],
      isCertainPathOnly: true,
      children: eventRegistrationConfig.navigation,
    },
    {
      name: "Data Connector",
      internalName: "data-connector",
      href: "#",
      icon: ServerStackIcon,
      current: false,
      permissions: ["data-connector"],
      children: dataConnectorConfig.navigation,
    },
    {
      name: "Admin",
      internalName: "company-admin",
      href: "#",
      icon: Cog6ToothIcon,
      current: false,
      roles: ["ROLE_OWNER_GM", "ROLE_SUPER_ADMIN"],
      isNonCertainPathOnly: true,
      children: [
        {
          name: "User Management",
          href: "/hub/users",
          icon: UsersIcon,
          current: false,
          internalName: "company_user_management",
          roles: ["ROLE_OWNER_GM", "ROLE_SUPER_ADMIN"],
          isNonCertainPathOnly: true,
        },
        {
          name: "Games",
          href: "#",
          icon: AcademicCapIcon,
          current: false,
          internalName: "games_admin",
          roles: ["ROLE_SUPER_ADMIN", "ROLE_LEARNING_ADMIN"],
          isNonCertainPathOnly: true,
          children: [
            {
              name: "Scenario Admin",
              href: "/games/admin/scenarios",
              icon: AcademicCapIcon,
              current: false,
              internalName: "scenario_admin_company",
              roles: ["ROLE_SUPER_ADMIN", "ROLE_LEARNING_ADMIN"],
              isNonCertainPathOnly: true,
            },
            {
              name: "Play Game",
              href: "/games/door-to-done",
              icon: AcademicCapIcon,
              current: false,
              internalName: "play_game_company",
              roles: ["ROLE_SUPER_ADMIN", "ROLE_LEARNING_ADMIN"],
              isNonCertainPathOnly: true,
            },
          ],
        },
      ],
    },
  ];
}
